/**
 * Calculates the appropriate page number to navigate to after a mutation that may have
 * removed records from the current result set (e.g., approving records that fall out of a filter).
 * 
 * @param currentPage - The page number the user is currently on (1-based)
 * @param pageSize - Number of records per page
 * @param newTotalCount - The total count of records after the mutation
 * @returns The page number to navigate to (1-based), ensuring it's within valid range
 * 
 * @example
 * // User is on page 5 with 10 records per page, 50 total records
 * // After approving 15 records, only 35 remain (4 pages)
 * // This will return 4 (the last valid page)
 * const adjustedPage = getAdjustedPageAfterMutation(5, 10, 35);
 */
export function getAdjustedPageAfterMutation(
  currentPage: number,
  pageSize: number,
  newTotalCount: number
): number {
  // If no records remain, go to page 1
  if (newTotalCount === 0) {
    return 1;
  }

  // Calculate the last valid page
  const lastValidPage = Math.ceil(newTotalCount / pageSize);

  // If current page is still valid, stay on it
  if (currentPage <= lastValidPage) {
    return currentPage;
  }

  // Otherwise, go to the last valid page
  return lastValidPage;
}

/**
 * Hook-friendly wrapper that returns a function to safely refresh list data
 * after a mutation, automatically adjusting the page if needed.
 * 
 * @param fetchItems - The function that fetches items (should return response with totalCount)
 * @param currentPage - The current page number
 * @param pageSize - Number of records per page
 * @param searchFilters - Current search filters
 * @param setCurrentPage - Function to update the current page in state
 * @returns An async function that fetches data and adjusts page if needed
 * 
 * @example
 * const refreshAfterMutation = useRefreshAfterMutation(
 *   fetchItems,
 *   currentPage,
 *   pageSize,
 *   searchFilters,
 *   setCurrentPage
 * );
 * 
 * // After a mutation:
 * await refreshAfterMutation();
 */
export function useRefreshAfterMutation<TFilters = any>(
  fetchItems: (page: number, size: number, filters: TFilters) => Promise<void>,
  currentPage: number,
  pageSize: number,
  searchFilters: TFilters,
  setCurrentPage: (page: number) => void,
  getTotalCount: () => number
) {
  return async (): Promise<void> => {
    // First, fetch with current page to get updated total count
    await fetchItems(currentPage, pageSize, searchFilters);
    
    // Get the new total count from state
    const newTotalCount = getTotalCount();
    
    // Calculate the adjusted page
    const adjustedPage = getAdjustedPageAfterMutation(currentPage, pageSize, newTotalCount);
    
    // If page needs adjustment, update state and refetch
    if (adjustedPage !== currentPage) {
      setCurrentPage(adjustedPage);
      await fetchItems(adjustedPage, pageSize, searchFilters);
    }
  };
}

/**
 * Simpler approach: Just calculate and return the adjusted page without the hook wrapper.
 * Use this when you want more control over the refresh flow.
 * 
 * @example
 * await updateStatus(dto);
 * const tempResponse = await fetchItems(currentPage, pageSize, searchFilters);
 * const adjustedPage = getAdjustedPageAfterMutation(currentPage, pageSize, tempResponse.totalCount);
 * if (adjustedPage !== currentPage) {
 *   setCurrentPage(adjustedPage);
 *   await fetchItems(adjustedPage, pageSize, searchFilters);
 * }
 */
